const RETINA_SCALE = 2;

/** Creates a retina-scaled canvas context. Returns null outside the browser */
const createScaledCanvas = (
  width: number,
  height: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null => {
  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width = width * RETINA_SCALE;
  canvas.height = height * RETINA_SCALE;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.scale(RETINA_SCALE, RETINA_SCALE);

  return { canvas, ctx };
};

/** Exports a canvas to a PNG Uint8Array */
const canvasToPng = async (
  canvas: HTMLCanvasElement,
): Promise<Uint8Array | null> => {
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/png'),
  );

  return blob ? new Uint8Array(await blob.arrayBuffer()) : null;
};

/** Loads an image from a URL into an HTMLImageElement */
const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/** Converts SVG markup to a PNG Uint8Array via Canvas. Browser only */
export const svgToImageBuffer = async (
  svg: string,
  width: number,
  height: number,
): Promise<Uint8Array | null> => {
  const result = createScaledCanvas(width, height);
  if (!result) return null;

  const { canvas, ctx } = result;
  const url = URL.createObjectURL(
    new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }),
  );

  try {
    const img = await loadImage(url);

    ctx.drawImage(img, 0, 0, width, height);

    return canvasToPng(canvas);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
};

/** Fetches an image, crops it into a circle via Canvas arc clip, and returns a PNG Uint8Array */
export const circularCropImageBuffer = async (
  url: string,
  size: number,
): Promise<Uint8Array | null> => {
  const result = createScaledCanvas(size, size);
  if (!result) return null;

  const { canvas, ctx } = result;
  let objectUrl = '';

  try {
    const response = await fetch(url);
    const blob = await response.blob();
    objectUrl = URL.createObjectURL(blob);

    const img = await loadImage(objectUrl);

    // Arc clip for circular crop
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    // Center-crop to fill the circle
    const { naturalWidth: w, naturalHeight: h } = img;
    const cropSize = Math.min(w, h);
    const srcX = (w - cropSize) / 2;
    const srcY = (h - cropSize) / 2;

    ctx.drawImage(img, srcX, srcY, cropSize, cropSize, 0, 0, size, size);

    return canvasToPng(canvas);
  } catch {
    return null;
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
};

/** Fetches an image from a URL and returns it as a Uint8Array */
export const fetchImageAsBuffer = async (
  url: string,
): Promise<Uint8Array | null> => {
  try {
    const arrayBuffer = await fetch(url).then((r) => r.arrayBuffer());

    return new Uint8Array(arrayBuffer);
  } catch {
    return null;
  }
};
